import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '@/components/common/PageHeader';
import { Tabs, type TabItem } from '@/components/common/Tabs';
import { FilterBar } from '@/components/common/FilterBar';
import { AsyncButton } from '@/components/common/AsyncButton';
import { DateRangePicker } from '@/components/forms/DateRangePicker';
import { Switch } from '@/components/forms/Switch';
import { useToast } from '@/components/common/toast';
import { useBootstrapTooltip } from '@/components/common/useBootstrapTooltip';
import { usePermission } from '@/permissions/usePermission';
import { ApiError } from '@/api/client/abpError';
import { reconciliationApi } from '@/api/services/reconciliation';
import {
  ReconciliationProviderScope,
  ReconciliationType,
  type DailyReconcileResultDto,
  type ReconcileResultDto,
} from '@/api/types/reconciliation';
import { ReconSection } from './ReconSection';
import { BufferControl } from './BufferControl';
import { sectionView, todayIso, yesterdayIso } from './helpers';
import './reconciliation.css';

const PERM_EXTERNAL = 'User.SafeguardingReconciliation.ExternalReconciliation';
const PERM_LP = 'User.SafeguardingReconciliation.Reconciliation.LpProvidersReconciliation';

const NO_SNAPSHOT = 'No snapshot available';
const LP_MISSING_TOAST =
  'No snapshot is available for the selected date. Please contact support for further assistance.';

type OuterTab = 'internal' | 'external' | 'lp';
type Scope = 'bank' | 'all';

interface BufferAmounts {
  bankBrokerProviders: number;
  allProviders: number;
}

export function ReconciliationPage() {
  const toast = useToast();
  const navigate = useNavigate();
  const can = usePermission();
  const canExternal = can(PERM_EXTERNAL);
  const canLp = can(PERM_LP);

  const [date, setDate] = useState(yesterdayIso());
  const [isLive, setIsLive] = useState(false);
  const [applied, setApplied] = useState<{ date: string; isLive: boolean } | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const [outer, setOuter] = useState<OuterTab>('internal');
  const [intScope, setIntScope] = useState<Scope>('bank');
  const [extScope, setExtScope] = useState<Scope>('bank');

  // The Excess/Shortfall result card carries a Bootstrap tooltip; initialise it here
  // (no global BS JS) and re-init whenever the rendered pane/result changes.
  const paneBodyRef = useRef<HTMLDivElement>(null);

  const lastDate = useRef(date);
  useEffect(() => {
    if (lastDate.current !== date) {
      lastDate.current = date;
      setIsLive(date === todayIso());
    }
  }, [date]);

  function onToggleLive(next: boolean) {
    setIsLive(next);
    if (next) {
      setDate(todayIso());
      lastDate.current = todayIso();
    } else if (date === todayIso()) {
      setDate(yesterdayIso());
      lastDate.current = yesterdayIso();
    }
  }

  // Reconcile query.
  const reconQuery = useQuery({
    queryKey: ['reconciliation', 'reconcile', applied, reloadKey],
    queryFn: () => reconciliationApi.reconcile({ date: applied!.date, isLive: applied!.isLive }),
    enabled: Boolean(applied),
    retry: false,
  });

  // Buffer amounts (Internal always; External when granted).
  const intBufferQuery = useQuery({
    queryKey: ['reconciliation', 'buffer', ReconciliationType.Internal],
    queryFn: () => reconciliationApi.getBufferAmounts(ReconciliationType.Internal),
  });
  const extBufferQuery = useQuery({
    queryKey: ['reconciliation', 'buffer', ReconciliationType.External],
    queryFn: () => reconciliationApi.getBufferAmounts(ReconciliationType.External),
    enabled: canExternal,
  });

  // Auto-fetch on mount; fix live/date mismatch before first load.
  useEffect(() => {
    const effectiveLive = isLive && date === todayIso();
    if (isLive && date !== todayIso()) setIsLive(false);
    setApplied({ date, isLive: effectiveLive });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // LP missing-snapshot detection + toast.
  const lpMissing = Boolean(reconQuery.data && canLp && reconQuery.data.lp == null);
  useEffect(() => {
    if (lpMissing && outer === 'lp') toast.error(LP_MISSING_TOAST);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lpMissing, outer, reconQuery.dataUpdatedAt]);

  useEffect(() => {
    if (reconQuery.isError) {
      toast.error(reconQuery.error instanceof ApiError ? reconQuery.error.message : 'Failed to reconcile.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reconQuery.isError, reconQuery.error]);

  function fetchData() {
    const effectiveLive = isLive && date === todayIso();
    if (isLive && date !== todayIso()) setIsLive(false);
    setApplied({ date, isLive: effectiveLive });
    setReloadKey((k) => k + 1);
  }

  async function exportExcel() {
    if (!applied) return;
    try {
      await reconciliationApi.downloadExcel(applied, 'Daily Reconciliation.xlsx');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Failed to export Excel.');
    }
  }
  async function exportPdf() {
    if (!applied) return;
    try {
      await reconciliationApi.downloadPdf(applied, 'Daily Reconciliation.pdf');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Failed to export PDF.');
    }
  }

  async function saveBuffer(type: ReconciliationType, scope: ReconciliationProviderScope, amount: number) {
    try {
      await reconciliationApi.saveBufferAmount({ reconciliationType: type, providerScope: scope, amount });
      toast.success('Buffer Amount saved successfully.');
      if (type === ReconciliationType.Internal) await intBufferQuery.refetch();
      else await extBufferQuery.refetch();
      setReloadKey((k) => k + 1); // re-reconcile
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Failed to save the Buffer Amount.');
    }
  }

  // Drilldown routing helper: append ?live=1 or ?date=<iso>.
  function qp(): string {
    return applied?.isLive ? '?live=1' : `?date=${applied?.date ?? date}`;
  }

  const data: DailyReconcileResultDto | undefined = reconQuery.data;

  useBootstrapTooltip(paneBodyRef, [data, outer, intScope, extScope, reconQuery.dataUpdatedAt]);

  const tabs: TabItem[] = [{ key: 'internal', title: 'Internal Reconciliation' }];
  if (canExternal) tabs.push({ key: 'external', title: 'External Reconciliation' });
  if (canLp) tabs.push({ key: 'lp', title: 'Assets/Liabilities' });

  return (
    <div className="reconciliation-page">
      <PageHeader title="Reconciliation" breadcrumb={['Home', 'Reconciliation']} />

      <FilterBar
        fields={[
          {
            node: <DateRangePicker label="Reference Date" mode="single" value={date} maxDate={todayIso()} onChange={setDate} />,
            width: 240,
            pinned: true,
          },
        ]}
        modes={<Switch label="Live Data" checked={isLive} onChange={onToggleLive} />}
        actions={
          <>
            <AsyncButton className="btn btn-primary btn-sm" icon="fa fa-search" onClick={fetchData} loading={reconQuery.isFetching}>
              Fetch
            </AsyncButton>
            <AsyncButton className="btn btn-outline-primary btn-sm" icon="fa fa-print" onClick={exportExcel}>
              Reconciliation Statement Excel
            </AsyncButton>
            <AsyncButton className="btn btn-outline-primary btn-sm" icon="fa fa-print" onClick={exportPdf}>
              Reconciliation Statement Pdf
            </AsyncButton>
          </>
        }
      />

      <div className="card mt-2 reconciliation-card">
        <div className="card-header p-0">
          <Tabs tabs={tabs} active={outer} onChange={(k) => setOuter(k as OuterTab)} variant="underline" />
        </div>
        <div className="card-body" ref={paneBodyRef}>
          {!data && <div className="text-muted">{reconQuery.isFetching ? 'Fetching…' : 'Fetch to load reconciliation.'}</div>}

          {data && outer === 'internal' && (
            <InternalExternalPane
              result={data.internal}
              buffers={intBufferQuery.data}
              type={ReconciliationType.Internal}
              scope={intScope}
              onScope={setIntScope}
              onSaveBuffer={saveBuffer}
              navigate={navigate}
              qp={qp}
            />
          )}

          {data && outer === 'external' && canExternal && data.external && (
            <InternalExternalPane
              result={data.external}
              buffers={extBufferQuery.data}
              type={ReconciliationType.External}
              scope={extScope}
              onScope={setExtScope}
              onSaveBuffer={saveBuffer}
              navigate={navigate}
              qp={qp}
            />
          )}

          {data && outer === 'lp' && canLp && (
            <LpPane
              data={data}
              navigate={navigate}
              isLive={applied?.isLive ?? false}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// --- Internal / External pane: inner pills + buffer + section ---
interface PaneProps {
  result: ReconcileResultDto;
  buffers: BufferAmounts | undefined;
  type: ReconciliationType;
  scope: Scope;
  onScope: (s: Scope) => void;
  onSaveBuffer: (type: ReconciliationType, scope: ReconciliationProviderScope, amount: number) => Promise<void>;
  navigate: ReturnType<typeof useNavigate>;
  qp: () => string;
}

function InternalExternalPane({ result, buffers, type, scope, onScope, onSaveBuffer, navigate, qp }: PaneProps) {
  const isInternal = type === ReconciliationType.Internal;
  const pane = isInternal ? (scope === 'bank' ? 'int-bank' : 'int-all') : scope === 'bank' ? 'ext-bank' : 'ext-all';
  const view = sectionView(result, pane as 'int-bank' | 'int-all' | 'ext-bank' | 'ext-all');

  const assetsLabel = isInternal
    ? scope === 'bank'
      ? 'Banks Assets'
      : 'Bank/Broker/EMI/PSP Assets'
    : 'Real Cash Balance RC';

  const bufferAmount = scope === 'bank' ? (buffers?.bankBrokerProviders ?? 0) : (buffers?.allProviders ?? 0);
  const providerScope = scope === 'bank' ? ReconciliationProviderScope.BankBrokerProviders : ReconciliationProviderScope.AllProviders;

  const pillTabs: TabItem[] = [
    { key: 'bank', title: 'Bank/Broker Providers' },
    { key: 'all', title: 'Bank/Broker/EMI/PSP Providers' },
  ];

  const toProvidersAssets = () => navigate(`/User/DailyReconciliation/ProvidersAssets${qp()}`);
  const toClientsAssets = () => navigate(`/User/DailyReconciliation/ClientsAssets${qp()}`);

  return (
    <div>
      <div className="d-flex flex-wrap align-items-end justify-content-between gap-2 mb-3">
        <Tabs tabs={pillTabs} active={scope} onChange={(k) => onScope(k as Scope)} variant="pills" />
        <BufferControl amount={bufferAmount} onSave={(amt) => onSaveBuffer(type, providerScope, amt)} />
      </div>
      <ReconSection
        view={view}
        assetsLabel={assetsLabel}
        liabilitiesValue={result.liabilities}
        onAssetsClick={toProvidersAssets}
        onLiabilitiesClick={toClientsAssets}
      />
    </div>
  );
}

// --- LP pane ---
function LpPane({
  data,
  navigate,
  isLive,
}: {
  data: DailyReconcileResultDto;
  navigate: ReturnType<typeof useNavigate>;
  isLive: boolean;
}) {
  const lp = data.lp;
  const liabilities = data.internal.liabilities;

  if (!lp) {
    // No snapshot: zeroed section, liabilities still shown, assets not clickable.
    const view = { assetsValue: 0, result: 0, title: NO_SNAPSHOT, message: '' };
    return <ReconSection view={view} assetsLabel="Sum of Equities - LP Providers" liabilitiesValue={liabilities} onAssetsClick={null} onLiabilitiesClick={() => navigate(`/User/DailyReconciliation/ClientsAssets${isLive ? '?live=1' : ''}`)} />;
  }

  const view = sectionView(lp, 'lp');

  const assetsClick =
    isLive
      ? () => navigate('/User/RiskMonitoring/LPAccounts')
      : lp.lpSnapshotId && lp.lpSnapshotId !== 0
        ? () =>
            navigate(
              `/User/RiskMonitoring/Overnights/LPAccounts/Financials?snapshotId=${lp.lpSnapshotId}&snapshotAt=${encodeURIComponent(lp.lpSnapshotAt ?? '')}`,
            )
        : null;

  return (
    <ReconSection
      view={view}
      assetsLabel="Sum of Equities - LP Providers"
      liabilitiesValue={liabilities}
      onAssetsClick={assetsClick}
      onLiabilitiesClick={() => navigate(`/User/DailyReconciliation/ClientsAssets${isLive ? '?live=1' : ''}`)}
    />
  );
}
